import React, { useEffect, useMemo, useState } from 'react';
import { getConfig } from '../config';

type Row = { workspaceName: string; month: string; newUsers: number };

function getLast12Months(): { key: string; label: string }[] {
  const months: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString(undefined, { month: 'short' });
    months.push({ key, label });
  }
  return months;
}

const NewUsersByMonthChart: React.FC = () => {
  const { apiBase } = getConfig();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Use global endpoint that aggregates across all workspaces
        const res = await fetch(`${apiBase}/superadmin/analytics/new-users-by-month-global`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = (await res.json()) as Array<{ month: string; newUsers: number }>;
        // Map to the Row-like shape for downstream logic
        setRows(data.map(d => ({ workspaceName: 'All', month: d.month, newUsers: d.newUsers })));
      } catch (e: any) {
        setErr(e?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  const months = useMemo(() => getLast12Months(), []);

  // Aggregate across workspaces and fill missing months with 0
  const series = useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of rows) {
      totals.set(r.month, (totals.get(r.month) ?? 0) + Number(r.newUsers || 0));
    }
    return months.map(({ key }) => totals.get(key) ?? 0);
  }, [rows, months]);

  const max = Math.max(1, ...series);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="text-red-600">Error: {err}</div>;

  return (
    <div className="bg-white shadow p-4 rounded border border-gray-100">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">New Users (last 12 months)</h2>
      </div>
      <div className="h-40 grid grid-cols-12 items-end gap-1 sm:gap-2">
        {series.map((v, idx) => {
          const pct = Math.max(4, (v / max) * 100);
          return (
            <div key={idx} className="h-full flex flex-col items-center justify-end gap-1 min-w-0">
              <div className="relative w-full h-full">
                <div
                  className="absolute left-0 right-0 rounded-t bg-mc-amber"
                  style={{ height: `${pct}%`, bottom: 0 }}
                  title={`${v}`}
                />
                <div
                  className="absolute left-0 right-0 text-center text-[9px] sm:text-[10px] text-gray-700"
                  style={{ bottom: `calc(${pct}% + 2px)` }}
                >
                  {v}
                </div>
              </div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 truncate w-full text-center">{months[idx].label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewUsersByMonthChart;


